import { create } from 'zustand';
import type { Workout, Exercise, ExerciseSet } from '../types';
import { workoutApi, exerciseApi } from '../services/api';

// Set data types for different set types
type StandardSetData = {
  setType: 'standard';
  reps: number;
  weight: number;
  difficulty: number;
};

type CardioSetData = {
  setType: 'cardio';
  durationSeconds: number;
  resistanceLevel?: number;
  difficulty: number;
};

type DualHandSetData = {
  setType: 'dualHand';
  leftReps: number;
  rightReps: number;
  leftWeight: number;
  rightWeight: number;
  difficulty: number;
};

export type AddSetData = StandardSetData | CardioSetData | DualHandSetData;

export type UpdateSetData = Partial<{
  reps: number;
  weight: number;
  difficulty: number;
  durationSeconds: number;
  resistanceLevel: number;
  leftReps: number;
  rightReps: number;
  leftWeight: number;
  rightWeight: number;
}>;

interface WorkoutState {
  // State
  activeWorkout: Workout | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadActiveWorkout: () => Promise<void>;
  startWorkout: (startMood?: number, startTime?: string) => Promise<Workout>;
  endWorkout: (data?: { bodyWeight?: number; endMood?: number; notes?: string; endTime?: string }) => Promise<void>;
  addExercise: (data: { machineId?: string; bodyweightExerciseId?: string }) => Promise<Exercise>;
  addSet: (exerciseId: string, data: AddSetData) => Promise<ExerciseSet>;
  updateSet: (setId: string, data: UpdateSetData) => Promise<ExerciseSet>;
  deleteSet: (setId: string, exerciseId: string) => Promise<void>;
  updateExerciseInWorkout: (exerciseId: string, data: { machineId?: string; bodyweightExerciseId?: string }) => Promise<Exercise>;
  deleteExercise: (exerciseId: string) => Promise<void>;
  clearWorkout: () => void;
}

export const useWorkoutStore = create<WorkoutState>((set, get) => ({
  // Initial state
  activeWorkout: null,
  isLoading: false,
  error: null,

  // Load active workout
  loadActiveWorkout: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await workoutApi.getActive();
      
      if (response.success) {
        set({
          activeWorkout: response.data?.workout || null,
          isLoading: false,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load workout';
      set({ isLoading: false, error: message });
    }
  },

  // Start a new workout
  startWorkout: async (startMood?: number, startTime?: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await workoutApi.start(startMood, startTime);
      
      if (response.success && response.data) {
        const workout = { ...response.data.workout, exercises: [] };
        set({
          activeWorkout: workout,
          isLoading: false,
        });
        return workout;
      }
      throw new Error('Failed to start workout');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to start workout';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  // End the current workout
  endWorkout: async (data?: { bodyWeight?: number; endMood?: number; notes?: string; endTime?: string }) => {
    const { activeWorkout } = get();
    if (!activeWorkout) return;

    set({ isLoading: true, error: null });
    
    try {
      await workoutApi.end(activeWorkout.id, data);
      set({
        activeWorkout: null,
        isLoading: false,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to end workout';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  // Add exercise to workout
  addExercise: async (data: { machineId?: string; bodyweightExerciseId?: string }) => {
    const { activeWorkout } = get();
    if (!activeWorkout) throw new Error('No active workout');

    try {
      const response = await workoutApi.addExercise(activeWorkout.id, data);
      
      if (response.success && response.data) {
        const exercise = response.data.exercise;
        set({
          activeWorkout: {
            ...activeWorkout,
            exercises: [...activeWorkout.exercises, exercise],
          },
        });
        return exercise;
      }
      throw new Error('Failed to add exercise');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add exercise';
      set({ error: message });
      throw error;
    }
  },

  // Add set to exercise
  addSet: async (exerciseId: string, data: AddSetData) => {
    const { activeWorkout } = get();
    if (!activeWorkout) throw new Error('No active workout');

    try {
      const response = await exerciseApi.addSet(exerciseId, data);
      
      if (response.success && response.data) {
        const newSet = response.data.set;
        
        // Update the exercise with the new set
        set({
          activeWorkout: {
            ...activeWorkout,
            exercises: activeWorkout.exercises.map((ex) =>
              ex.id === exerciseId
                ? { ...ex, sets: [...ex.sets, newSet] }
                : ex
            ),
          },
        });
        
        return newSet;
      }
      throw new Error('Failed to add set');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add set';
      set({ error: message });
      throw error;
    }
  },

  // Update set
  updateSet: async (setId: string, data: UpdateSetData) => {
    const { activeWorkout } = get();
    if (!activeWorkout) throw new Error('No active workout');

    try {
      const response = await exerciseApi.updateSet(setId, data);
      
      if (response.success && response.data) {
        const updatedSet = response.data.set;
        
        // Update the set in the workout
        set({
          activeWorkout: {
            ...activeWorkout,
            exercises: activeWorkout.exercises.map((ex) => ({
              ...ex,
              sets: ex.sets.map((s) => s.id === setId ? updatedSet : s),
            })),
          },
        });
        
        return updatedSet;
      }
      throw new Error('Failed to update set');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update set';
      set({ error: message });
      throw error;
    }
  },

  // Delete set
  deleteSet: async (setId: string, exerciseId: string) => {
    const { activeWorkout } = get();
    if (!activeWorkout) throw new Error('No active workout');

    try {
      const response = await exerciseApi.deleteSet(setId);
      
      if (response.success) {
        // Remove the set from the workout
        set({
          activeWorkout: {
            ...activeWorkout,
            exercises: activeWorkout.exercises.map((ex) =>
              ex.id === exerciseId
                ? { ...ex, sets: ex.sets.filter((s) => s.id !== setId) }
                : ex
            ),
          },
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete set';
      set({ error: message });
      throw error;
    }
  },

  // Swap exercise machine/bodyweight
  updateExerciseInWorkout: async (exerciseId: string, data: { machineId?: string; bodyweightExerciseId?: string }) => {
    const { activeWorkout } = get();
    if (!activeWorkout) throw new Error('No active workout');

    try {
      const response = await exerciseApi.updateExercise(exerciseId, data);

      if (response.success && response.data) {
        const updated = response.data.exercise;
        set({
          activeWorkout: {
            ...activeWorkout,
            exercises: activeWorkout.exercises.map((ex) =>
              ex.id === exerciseId ? updated : ex
            ),
          },
        });
        return updated;
      }
      throw new Error('Failed to update exercise');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update exercise';
      set({ error: message });
      throw error;
    }
  },

  // Delete exercise from workout
  deleteExercise: async (exerciseId: string) => {
    const { activeWorkout } = get();
    if (!activeWorkout) throw new Error('No active workout');

    try {
      const response = await exerciseApi.deleteExercise(exerciseId);

      if (response.success) {
        set({
          activeWorkout: {
            ...activeWorkout,
            exercises: activeWorkout.exercises.filter((ex) => ex.id !== exerciseId),
          },
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete exercise';
      set({ error: message });
      throw error;
    }
  },

  // Clear workout state
  clearWorkout: () => {
    set({ activeWorkout: null, error: null });
  },
}));
